import React, { useEffect, useRef, useState } from "react";
import hotkeys from "hotkeys-js";
import { marked } from "marked";
import InputField from "@ingka/input-field";
import Button from "@ingka/button";
import HelperText from "@ingka/helper-text";
import Loading, { LoadingBall } from "@ingka/loading";
import Modal, { Sheets } from "@ingka/modal";
import FormField from '@ingka/form-field';
import { useAgentGlue } from "./useAgentGlue";
import "./style.scss";

// CustomEvent polyfill for TypeScript
declare global {
  interface HTMLElementEventMap {
    PROMPT_SUBMIT: CustomEvent<{ message: string }>;
  }
}

export const ChatBox = () => {
  const [open, setOpen] = useState(true);
  const [prompt, setPrompt] = useState("");
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const sheetRef = useRef<any>(null);
  const containerRef = useRef<any>(null);

  const {
    messages,
    isThinking,
    thinkingMessage,
    submitPrompt,
  } = useAgentGlue();

  useEffect(() => {
    const scrollable = (containerRef.current as unknown as HTMLDivElement)?.querySelector(
      ".scrollable-content"
    );
    if (scrollable) {
      scrollable.scrollTo({ top: scrollable.scrollHeight, behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    hotkeys("ctrl+k, command+k", (e) => {
      e.preventDefault();
      setOpen((prev) => !prev);
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    });

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "attributes" && mutation.attributeName === "open") {
          setOpen((mutation.target as any).open);
        }
      });
    });

    if (sheetRef.current) {
      observer.observe(sheetRef.current as any, {
        attributes: true,
      });
    }

    return () => {
      hotkeys.unbind("ctrl+k, command+k");
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    if (scrollContainerRef.current) {
      setTimeout(() => {
        scrollContainerRef.current!.scrollTo({
          top: scrollContainerRef.current!.scrollHeight,
          behavior: "smooth",
        });
      }, 0);
    }
  }, [messages, isThinking]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) return;

    // Dispatch prompt to external handler (if needed)
    const event = new CustomEvent("PROMPT_SUBMIT", {
      detail: { message: prompt },
    });
    (containerRef.current as any)?.dispatchEvent(event);

    submitPrompt(prompt);
    setPrompt("");
  };

  return (
    <div ref={containerRef}>
    <Modal visible={open} aria-label="Chat Box" handleCloseBtn={() => setOpen(false)}>
      <Sheets open={open} aria-label="Chat Sheet" size="small" header={null} footer={null}>
        <div ref={sheetRef} className="flex flex-col h-full pt-2" style={{padding: 20}}>
          {/* Messages */}
          <div
            ref={scrollContainerRef}
            className="flex-1 flex flex-col overflow-y-scroll scrollable-content"
          >
            {messages.length === 0 ? (
              <div className="no-message-notice">
                <p>Start a conversation by typing in the input field below.</p>
                <p>
                  Press <kbd>Ctrl/Cmd</kbd> + <kbd>K</kbd> to toggle the chat
                  <br />
                  Press <kbd>Ctrl/Cmd</kbd> + <kbd>J</kbd> to toggle the chat
                  mode
                  <br />
                  Press <kbd>Enter</kbd> to send a message
                </p>
              </div>
            ) : null}

            {messages.map((message, index) => (
              <div
                key={index}
                className={`message-wrapper ${["user", "assistant"].includes(message.role) ? message.role : ""}`}
              >
                <div
                  className="message [&_td]:p-1 [&_tr]:border-b [&_tr]:border-gray-200"
                  dangerouslySetInnerHTML={{
                    __html: marked.parse(message.content) as string,
                  }}
                />
              </div>
            ))}

            {isThinking && (
              <div id="loading-container">
                <div id="loading-ball">
                  <Loading
                    className="ingka-loading--custom-class"
                    text={thinkingMessage}
                    labelTransitions
                  >
                    <LoadingBall />
                  </Loading>
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="scrollable-footer">
            <form onSubmit={handleSubmit} className="flex flex-row">
            <FormField
            valid="error"
            validation={{
              msg: 'This field is required',
              type: 'error',
            }}
          >
              <InputField
                id="prompt-input"
                ref={inputRef}
                type="text"
                value={prompt}
                placeholder="Please type your message here"
                onChange={(e) => setPrompt(e.target.value)}
              />
              {messages.length === 0 && (
                <HelperText slot="helper">
                  Please type a message to get started.
                </HelperText>
              )}
              </FormField>
              <Button
                htmlType="submit"
                type="emphasised"
                size="small"
                className="m-1"
              >
                Send
              </Button>
            </form>
          </div>
        </div>
      </Sheets>
    </Modal>
    </div>
  );
};